/** System-fields W5 — the ONE place the frontend decides what a system column IS.
 *
 * Spec: `docs/plans/strategic-architecture/crm-system-and-activity-fields-spec.md` §9.2, §10, §12.4.
 *
 * ## Why this module exists at all
 *
 * W5 flips the server's column `origin` from `"derived"` to `"system"` (§9.2). Five frontend call
 * sites branched on the old string, and each one would have answered wrongly:
 *
 * | site | old test | what the flip alone would have done |
 * |---|---|---|
 * | `crmEditability.panelFieldEditor` | `origin === 'derived'` → not editable | made every system cell EDITABLE in the panel |
 * | `CrmListGrid.buildColumnDefs` | `isDerived` gates the cell editor | same, in the grid |
 * | `CrmListGrid` date formatter | `isDerived && type==='date'` | dropped the relative-day render + exact tooltip |
 * | `CrmColumnHeader` | `canEdit` allows `origin==='system'` | offered RENAME/DELETE on a computed column |
 * | `CrmColumnsPanel` | `origin !== 'derived'` = "list specific" | filed system fields under the operator's own columns |
 *
 * ⚠ **The trap, and the reason none of this may key on `origin`.** `origin === 'system'` is NOT
 * unique to this catalog: `crm_field_def.origin` has carried `"system"` since `create_list` for the
 * STORED, EDITABLE `name` column on every opportunity list — the IOT's deal name — and the
 * entity catch-all columns and the narrative-block field defs mint it too. A consumer that read the
 * string as "read-only" would freeze the deal-name cell on the primary list. So:
 *
 *   * **read-only-ness** comes from `read_only` — the capability §9.2 says to use;
 *   * **catalog membership** comes from `system_family` — present ⇒ a system field.
 *
 * `origin` is descriptive metadata now. Nothing in the frontend should branch on it again.
 */
import type { CrmGridColumn } from '../../types';

/** The four §10.1 Customize View groups, in the order the panel renders them. */
export const SYSTEM_FAMILIES = ['record', 'ai_review', 'activity', 'notes'] as const;
export type SystemFamily = (typeof SYSTEM_FAMILIES)[number];

export const SYSTEM_FAMILY_LABELS: Record<string, string> = {
  record: 'Record',
  ai_review: 'AI review',
  activity: 'Activity',
  notes: 'Notes',
};

/**
 * Is this a SYSTEM-FIELD column — one served by the server-owned catalog?
 *
 * Keys on `system_family`, never on `origin` (see the module header) and never on a list of field
 * keys, so a catalog addition needs no frontend release.
 */
export function isSystemColumn(col: Pick<CrmGridColumn, 'system_family'> | null | undefined): boolean {
  return !!col?.system_family;
}

/**
 * Is this column COMPUTED — never inline-editable, no rename/delete, dates rendered relatively?
 *
 * The union of three things that were previously conflated under one string:
 *   * `read_only: true` — the server said so. Every catalog column carries it (§9.2);
 *   * `origin === 'derived'` — the always-on identity columns (name/email/title/org/domain), which
 *     are graph-computed and are NOT system fields (they carry no `system_family`);
 *   * `origin === 'view'` — a client-side view-only column (`crmAiProposedRow`'s injected column
 *     during the compatibility window).
 *
 * ⚠ Deliberately NOT `origin === 'system'`. That would catch the IOT's stored, editable `name`
 * column and make the deal name uneditable — see the module header.
 */
export function isComputedColumn(col: CrmGridColumn | null | undefined): boolean {
  if (!col) return false;
  return col.read_only === true || col.origin === 'derived' || col.origin === 'view';
}

/**
 * The DISPLAY text for a raw enum value (§10.3, §12.4, §20 Q9) — the label-layer migration.
 *
 * `AI proposed` renders as `Proposed`. The RAW value is untouched and stays what filters store, what
 * sort compares and what the API returns, because it is a released contract (W2 shipped it and W0
 * measured two prod saved views referencing the key). §12.4's saved-filter migration is a no-op for
 * exactly that reason — nothing stored the label.
 *
 * The map comes from the SERVER, on the column's `enum_labels` (`system_fields.ENUM_DISPLAY_LABELS`),
 * so a future vocabulary change is one backend constant and no frontend release. No map, or no entry
 * for this value ⇒ render the raw value, which is what every other enum column does.
 */
export function enumDisplay(col: CrmGridColumn | null | undefined, raw: unknown): string {
  const s = raw == null ? '' : String(raw);
  const labels = col?.enum_labels;
  if (!labels) return s;
  return labels[s] ?? s;
}

/**
 * W5 (§7.3): the per-row COVERAGE state for one system field, for its definition tooltip.
 *
 * The row carries TWO meta blobs, one per family — `_system_record_state` (W3's rollup) and
 * `_system_activity_state` (W5's deal-activity generation) — because they are written by different
 * producers on different clocks. Collapsing them would make one family's freshness a claim about the
 * other's, so this picks the one that governs the column.
 *
 * Which blob governs is decided by the column's FAMILY, never by a list of field keys the frontend
 * would have to grow every time the catalog does (§6's rule, applied to presentation).
 */
export function systemCoverageFor(
  data: Record<string, unknown> | undefined,
  col: Pick<CrmGridColumn, 'field_key' | 'system_family'>,
  /** the caller's own date formatter, so this module owns no date policy */
  fmtDate: (v: string | null) => string,
): { coverage?: string | null; computedAt?: string | null } {
  if (col.system_family === 'activity') {
    const act = data?._system_activity_state as {
      coverage_state?: string; computed_at?: string | null;
      directional_coverage_state?: string; directional_keys?: string[];
    } | undefined;
    if (!act) return {};
    // The subtle half: on a pre-v2 rollup generation the three columns W4 ADDED are NULL because
    // they did not exist, which is `not_computed` — NOT "no email in that direction". The key list
    // comes off the row rather than being hardcoded here, so the backend stays the only place that
    // knows which columns those are and a future one needs no frontend release.
    const directional = (act.directional_keys ?? []).includes(col.field_key);
    return {
      coverage: directional ? act.directional_coverage_state : act.coverage_state,
      computedAt: fmtDate(act.computed_at ?? null) || null,
    };
  }
  if (col.system_family === 'record') {
    const rec = data?._system_record_state as {
      coverage_state?: string; computed_at?: string | null;
      record_modified_state?: string; modified_computed_at?: string | null;
    } | undefined;
    if (!rec) return {};
    // Record MODIFIED has its own state and its own clock, deliberately: W3 split them because the
    // backfill establishes the creation dates while only the producer derives the modified leg, so a
    // row can legitimately know its dates and never have had its modified date computed. Reporting
    // the row's single coverage for both would make such a row claim "no committed change is known"
    // about a record edited an hour ago. This is the one place a single key check is the right shape:
    // it is one FIELD with a second clock, not a family.
    if (col.field_key === RECORD_MODIFIED_AT_FIELD) {
      return { coverage: rec.record_modified_state,
               computedAt: fmtDate(rec.modified_computed_at ?? null) || null };
    }
    return { coverage: rec.coverage_state,
             computedAt: fmtDate(rec.computed_at ?? null) || null };
  }
  return {};
}

/**
 * The definition/freshness TOOLTIP for a system cell (§7.4, §14.3).
 *
 * Four things, in the order a reader needs them, and every one of them omitted when it is not known
 * rather than guessed:
 *
 *   1. the field's own definition (server-owned, `column.definition`);
 *   2. the exact timestamp, for a date cell — the caller passes it already formatted, because the
 *      grid and the panel format dates through their own shared helper;
 *   3. **what a blank MEANS**, which is §7.3/S12's whole point: `not_computed` says "we have not
 *      worked this out yet", a computed blank says "no such date is known". Printing neither, or
 *      printing "Never contacted", is the specific failure the coverage state exists to prevent;
 *   4. freshness — when the value was computed. §14.3's honest cadence for IOT activity is HOURLY
 *      (the `dash-activity-rollup` schedule fires at `40 * * * *` and the producer is a full
 *      recompute), and the tooltip says the computation time rather than promising a cadence.
 *
 * Never includes message subjects, snippets, attendees, attachment names or note text (§13.2): the
 * only inputs are the field's definition and its computation metadata.
 */
export function systemCellTooltip(
  col: CrmGridColumn | null | undefined,
  opts: {
    /** the exact date/time, pre-formatted by the caller's own date helper */
    exact?: string | null;
    /** `coverage_state` for this cell's family: 'computed' | 'not_computed' | 'unsupported' */
    coverage?: string | null;
    /** `computed_at`, pre-formatted */
    computedAt?: string | null;
    /** true when the cell is blank, so the coverage sentence is only shown where it explains something */
    isBlank?: boolean;
  } = {},
): string {
  const parts: string[] = [];
  if (opts.exact) parts.push(opts.exact);
  if (col?.definition) parts.push(col.definition);
  if (opts.isBlank) {
    if (opts.coverage === 'not_computed') {
      parts.push('Not calculated yet — this is not a statement about the record.');
    } else if (opts.coverage === 'computed') {
      parts.push('No such date is known.');
    }
  }
  if (opts.computedAt) parts.push(`Calculated ${opts.computedAt}.`);
  return parts.join(' · ');
}

/** The em dash §10.3/§12 require for a null system value — never `0`, never `Never`. */
export const SYSTEM_NULL_DISPLAY = '—';

/**
 * The two catalog keys the grid needs BY NAME, and the only two.
 *
 * Everything else keys on a capability (`read_only`, `system_family`, `enum_labels`) so the frontend
 * does not grow a list every time the catalog does. These two are named because each has a
 * presentation rule that is about the ONE field rather than about its family:
 *
 *   * the count opens the record on click (§10.3), which no other numeric column does;
 *   * Record modified reads a SECOND coverage clock (`record_modified_state`), because W3 split the
 *     clocks — the backfill writes creation dates, only the producer derives the modified leg.
 */
export const OPEN_AI_UPDATE_COUNT_FIELD = '__open_ai_update_count__';
export const RECORD_MODIFIED_AT_FIELD = '__record_modified_at__';

/**
 * One row of the Customize View "System fields" area, as the panel needs it.
 *
 * `enabled` is the LIST-level state (`available_system_fields[].enabled`) and `visible` is the
 * per-VIEW state (the grid's `hidden` set). §10.2 requires these to stay two visibly distinct
 * operations, which is why they are two fields here and not one boolean.
 */
export interface SystemFieldChoice {
  field_key: string;
  label: string;
  field_type: string;
  family: string;
  enabled: boolean;
  availability: string;
  definition?: string | null;
  read_only?: boolean;
}

/**
 * The §10.1 groups to render: only families with at least one OFFERABLE field, in group order.
 *
 * "Only eligible groups/fields appear" (§10.1). Eligibility is the server's answer, not a guess: an
 * `unsupported` field is already omitted from `available_system_fields`, and `backfill_required` is
 * dropped HERE — §9.3 says it plainly ("resolver exists but must not be shown yet"), and offering an
 * enable control for a column that can only render an em dash is the thing S12 and the 12.8% coverage
 * measurement are both about.
 *
 * `search` matches label OR definition, per §10.1's "search matches label and plain-language
 * definition" — a user who types "meeting" finds Next Meeting, and one who types "internal" finds
 * Last Contact through the sentence that says internal mail does not count.
 */
export function systemFieldGroups(
  choices: SystemFieldChoice[] | undefined,
  search: string,
): { family: SystemFamily; label: string; fields: SystemFieldChoice[] }[] {
  const needle = search.trim().toLowerCase();
  const matches = (c: SystemFieldChoice) =>
    !needle
    || c.label.toLowerCase().includes(needle)
    || (c.definition ?? '').toLowerCase().includes(needle);
  const offerable = (choices ?? []).filter((c) => c.availability === 'supported' && matches(c));
  return SYSTEM_FAMILIES
    .map((family) => ({
      family,
      label: SYSTEM_FAMILY_LABELS[family] ?? family,
      fields: offerable.filter((c) => c.family === family),
    }))
    .filter((grp) => grp.fields.length > 0);
}
